import React, { useEffect, useState, useMemo } from "react";
import { useDrag } from "react-dnd";
import { Input, Select } from "antd";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { useSelector, useDispatch } from "react-redux";
import { filterExercises } from "../../redux/Slices/toolboxExerciseSlice";
import Loader from "../../components/Common/Loader";
import useFetchCoachProfile from "../../utils/useFetchCoachProfile";
import ExercisePlaceholder from "../../styles/Assets/exercise_placeholder_100_100.png";

const ExerciseList = ({ isCustomized }) => {
  const { trans } = useCustomTranslation();
  useFetchCoachProfile();
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const { exerciseList, loading } = useSelector((state) => state.exercises);
  const { profileData } = useSelector((state) => state.profile);

  const coachId = profileData?.coachId;

  // Initial API call to load all exercises and recall when isCustomized changes
  useEffect(() => {
    if (coachId) {
      setInitialLoadComplete(false);
      setSelectedEquipment([]);
      setSelectedMuscleGroups([]);
      if (!exerciseList.length) {
      dispatch(
        filterExercises({
          coachId,
          name: "",
          isCustomized,
          equipment: [],
          muscleGroup: [],
        }),
      ).then(() => {
        setInitialLoadComplete(true);
      });
    }
    }
  }, [dispatch, coachId, isCustomized]);

  // Enhanced search function with flexible keyword matching
  const matchesSearch = (exerciseName, query) => {
    if (!query) return true;

    const normalizedName = exerciseName.toLowerCase();
    const normalizedQuery = query.toLowerCase().trim();

    // Split query into individual keywords
    const keywords = normalizedQuery
      .split(/\s+/)
      .filter((word) => word.length > 0);

    // Check if all keywords are present in the exercise name (order-independent)
    return keywords.every((keyword) => normalizedName.includes(keyword));
  };

  // Filter and sort exercises based on search and selected filters
  const filteredExercises = useMemo(() => {
    let result = [...exerciseList];

    // Apply search filter
    if (searchQuery) {
      result = result.filter((ex) => matchesSearch(ex.name, searchQuery));
    }

    // Apply equipment filter
    if (selectedEquipment.length > 0) {
      result = result.filter((ex) => selectedEquipment.includes(ex.equipment));
    }

    // Apply muscle group filter
    if (selectedMuscleGroups.length > 0) {
      result = result.filter((ex) =>
        selectedMuscleGroups.includes(ex.muscleGroups),
      );
    }

    // Sort exercises alphabetically by name
    return result.sort((a, b) => a.name.localeCompare(b.name));
  }, [exerciseList, searchQuery, selectedEquipment, selectedMuscleGroups]);

  // Derive filter options from currently filtered exercises
  const { equipmentOptions, muscleGroupOptions } = useMemo(() => {
    const equipmentSet = new Set();
    const muscleGroupSet = new Set();

    filteredExercises.forEach((exercise) => {
      if (exercise.equipment) {
        equipmentSet.add(exercise.equipment);
      }
      if (exercise.muscleGroups) {
        muscleGroupSet.add(exercise.muscleGroups);
      }
    });

    // Convert to sorted arrays
    return {
      equipmentOptions: Array.from(equipmentSet)
        .sort()
        .map((item) => ({
          value: item,
          label: item.charAt(0).toUpperCase() + item.slice(1),
        })),
      muscleGroupOptions: Array.from(muscleGroupSet)
        .sort()
        .map((item) => ({
          value: item,
          label: item.charAt(0).toUpperCase() + item.slice(1),
        })),
    };
  }, [filteredExercises]);

  return (
    <>
      <Input
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={trans("Common.searchByName")}
        allowClear
        className="rounded-3 search-box-exercise"
      />

      <div className="filter-section mt-2 mb-2">
        <Select
          mode="multiple"
          placeholder={
            trans("Common.select") + " " + trans("Exercise.Equipment")
          }
          value={selectedEquipment}
          onChange={setSelectedEquipment}
          className="w-100 mb-2"
          options={equipmentOptions}
        />

        <Select
          mode="multiple"
          placeholder={
            trans("Common.select") + " " + trans("Exercise.MuscleGroup")
          }
          value={selectedMuscleGroups}
          onChange={setSelectedMuscleGroups}
          className="w-100"
          options={muscleGroupOptions}
        />
      </div>

      <div className="exercise-list mt-2">
        {loading && !initialLoadComplete ? (
          <Loader />
        ) : filteredExercises.length === 0 ? (
          <p className="no-data-message">No data found</p>
        ) : (
          filteredExercises.map((exercise) => (
            <DraggableExercise key={exercise.exerciseId} exercise={exercise} />
          ))
        )}
      </div>
    </>
  );
};

const DraggableExercise = ({ exercise }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "EXERCISE",
    item: { exercise },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div ref={drag} className={`exercise-item ${isDragging ? "dragging" : ""}`}>
      {exercise.blobUrl ? (
        <img
          src={exercise.blobUrl}
          alt={exercise.name}
          className="exercise-image"
          loading="lazy"
        />
      ) : (
        <img
          src={ExercisePlaceholder}
          alt={exercise.name}
          className="excer-image"
        />
      )}
      <span>
        {exercise.name.charAt(0).toUpperCase() + exercise.name.slice(1)}
      </span>
    </div>
  );
};

export default ExerciseList;
